import React, { useContext, useEffect, useRef, useState } from "react";
import { httpGet, httpPost } from "../helpers/httpMethods";
import { UserDataContext } from "../../context/userContext";
import "./archive.css";

// "Papers with old issues" and one paper's old issues by month. Prices and buttons come from /archive_lookup,
// the same source the checkout uses, so what readers see is what they pay.
const PAGE = 10;
const FIRST_YEAR = 1960;
const MONTH_NAMES = ["January", "February", "March", "April", "May", "June", "July", "August", "September", "October", "November", "December"];
const naira = (v) => `₦${Number(v).toLocaleString("en-NG")}`;
const monthName = (m) => new Date(`${m}-01T12:00:00`).toLocaleDateString("en-GB", { month: "short", year: "numeric" });
const dayParts = (d) => {
  const x = new Date(`${d}T12:00:00`);
  return [x.toLocaleDateString("en-GB", { weekday: "short" }), x.toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" })];
};

// "Missing a year?": one-tap request for a year (or one month of it) that has no issues on TNS yet.
function AskYear({ paper, haveYears, onRequest, history }) {
  const { userIsLogin } = useContext(UserDataContext);
  const thisYear = new Date().getFullYear();
  const years = [];
  for (let y = thisYear; y >= FIRST_YEAR; y -= 1) if (!haveYears.includes(String(y))) years.push(String(y));
  const [year, setYear] = useState(years[0] || "");
  const [month, setMonth] = useState("");
  const [state, setState] = useState(null); // null | "busy" | { ok, text }
  if (!years.length) return null;
  const send = async () => {
    if (userIsLogin !== true) { history.push("/login"); return; }
    setState("busy");
    const span = month ? "month" : "year";
    const r = await httpPost("archive_requests", { publicationId: paper.id, span, dates: [month ? `${year}-${month}` : year] });
    if (!r || r.er) { setState({ ok: false, text: (r && r.er && (r.er.message || String(r.er))) || "Couldn’t send. Please try again." }); return; }
    const what = month ? `${MONTH_NAMES[Number(month) - 1]} ${year}` : `any issue from ${year}`;
    setState({ ok: true, text: r.data && r.data.created ? `Requested ${what}. We’ll email you when it’s here.` : `You’ve already asked for ${what}. We’ll email you.` });
  };
  return (
    <div className="ap-ask">
      <span className="ap-ask-text">
        <strong>Missing a year?</strong>
        Ask for any year from {FIRST_YEAR}. It’s free, and we’ll ask {paper.name} for it.
      </span>
      {state && state !== "busy" && state.ok ? (
        <span className="ap-ask-done" role="status">{state.text}</span>
      ) : (
        <span className="ap-ask-form">
          <select value={year} onChange={(e) => { setYear(e.target.value); setState(null); }} aria-label="Year">
            {years.map((y) => <option key={y} value={y}>{y}</option>)}
          </select>
          <select value={month} onChange={(e) => { setMonth(e.target.value); setState(null); }} aria-label="Month">
            <option value="">Any month</option>
            {MONTH_NAMES.map((mName, i) => <option key={mName} value={String(i + 1).padStart(2, "0")}>{mName}</option>)}
          </select>
          <button type="button" className="ap-act" disabled={state === "busy"} onClick={send}>{state === "busy" ? "Sending…" : userIsLogin === true ? "Request it free" : "Log in to request"}</button>
          <button type="button" className="ar-link" onClick={() => onRequest(paper)}>Exact dates instead</button>
          {state && state !== "busy" && !state.ok ? <span className="ar-error" role="alert">{state.text}</span> : null}
        </span>
      )}
    </div>
  );
}

function PaperView({ paper, onBack, onBuy, onRequest, history, reloadKey, initialMonth }) {
  const top = useRef(null);
  const [info, setInfo] = useState(null);
  const [month, setMonth] = useState(null);
  const [eds, setEds] = useState(null);
  const [shown, setShown] = useState(PAGE);
  const [status, setStatus] = useState({});
  const [note, setNote] = useState("");

  useEffect(() => { if (top.current) top.current.scrollIntoView({ behavior: "smooth", block: "start" }); }, [paper.id]);
  useEffect(() => {
    setInfo(null); setMonth(null); setEds(null);
    httpGet(`archive_papers/${paper.id}/months`).then((r) => {
      const d = r && r.data && r.data.months ? r.data : { months: [], total: 0 };
      setInfo(d);
      if (d.months.length) setMonth(initialMonth && d.months.some((x) => x.month === initialMonth) ? initialMonth : d.months[0].month);
    });
  }, [paper.id, initialMonth]);
  useEffect(() => {
    if (!month) return;
    setEds(null); setShown(PAGE); setStatus({});
    httpGet(`archive_papers/${paper.id}/editions?month=${month}`).then((r) => setEds(r && r.data && Array.isArray(r.data.editions) ? r.data.editions : []));
  }, [paper.id, month]);
  useEffect(() => {
    if (!eds || !eds.length) return undefined;
    let live = true;
    const days = [...new Set(eds.slice(0, shown).map((e) => e.day))];
    (async () => {
      const next = {};
      for (let i = 0; i < days.length; i += 10) {
        const r = await httpGet(`archive_lookup?publicationId=${paper.id}&dates=${days.slice(i, i + 10).join(",")}`);
        const d = r && r.data;
        const list = d && (Array.isArray(d.results) ? d.results : Array.isArray(d) ? d : []);
        (list || []).forEach((x) => { next[x.date] = x; });
      }
      if (live) setStatus(next);
    })();
    return () => { live = false; };
  }, [eds, shown, reloadKey, paper.id]);

  const jump = (value) => {
    if (!value || !info) return;
    const m = value.slice(0, 7);
    if (info.months.some((x) => x.month === m)) { setNote(""); setMonth(m); setShown(100); }
    else setNote({ day: value, text: `No old issue on ${dayParts(value).join(" ")}.` });
  };
  const cut = (info && info.paper && info.paper.cutoffDays) || paper.cutoffDays || 30;
  // Papers can go back decades (back issues from 1960), so months are picked within a year.
  const years = info ? [...new Set(info.months.map((m) => m.month.slice(0, 4)))] : [];
  const year = month ? month.slice(0, 4) : years[0];
  const yearMonths = info ? info.months.filter((m) => m.month.startsWith(year)) : [];
  const pickYear = (y) => { const m = info.months.find((x) => x.month.startsWith(y)); if (m) { setNote(""); setMonth(m.month); setShown(PAGE); } };
  const read = (id) => history.push(`/view_single_paper/${id}`);

  return (
    <section className="ap-view" ref={top} aria-labelledby="ap-title">
      <div className="ap-view-head">
        <span className="ap-logo lg">{paper.logo || paper.image ? <img src={paper.logo || paper.image} alt="" /> : null}</span>
        <div className="ap-view-text">
          <h2 id="ap-title" className="ar-h2">{paper.name} · old issues</h2>
          <span className="ap-note">
            {!info ? "Loading…" : [
              paper.oldest && paper.newest ? `${monthName(String(paper.oldest).slice(0, 7))} to ${monthName(String(paper.newest).slice(0, 7))}` : "",
              paper.fromPrice ? `from ${naira(paper.fromPrice)} each` : "",
            ].filter(Boolean).join(" · ")}
          </span>
        </div>
        <button type="button" className="ar-link" onClick={onBack}>Change paper</button>
      </div>
      <div className="ap-info">
        <strong>Editions from the last {cut} days</strong> aren’t sold here; they come with a{" "}
        <a href={`/publication/${encodeURIComponent(paper.name)}`} onClick={(e) => { e.preventDefault(); history.push(`/publication/${encodeURIComponent(paper.name)}`); }}>{paper.name} subscription</a>.
      </div>
      {info && info.months.length ? (
        <div className="ap-controls">
          {years.length > 1 ? (
            <label className="ap-year">
              <span>Year</span>
              <select value={year} onChange={(e) => pickYear(e.target.value)}>
                {years.map((y) => <option key={y} value={y}>{y}</option>)}
              </select>
            </label>
          ) : null}
          <div className="ap-months" role="tablist" aria-label="Month">
            {yearMonths.map((m) => (
              <button key={m.month} type="button" role="tab" aria-selected={month === m.month} className={`ap-month${month === m.month ? " on" : ""}`} onClick={() => { setNote(""); setMonth(m.month); }}>
                {years.length > 1 ? new Date(`${m.month}-01T12:00:00`).toLocaleDateString("en-GB", { month: "short" }) : monthName(m.month)}
              </button>
            ))}
          </div>
          <label className="ap-jump">
            <span>Go to a date</span>
            <input type="date" min="1960-01-01" onChange={(e) => jump(e.target.value)} />
          </label>
        </div>
      ) : null}
      {note ? (
        <p className="ar-result-note">
          {note.text}{" "}
          <button type="button" className="ar-link" onClick={() => onRequest(paper, { span: "day", dates: [note.day] })}>Request this date, free</button>
        </p>
      ) : null}
      {info && !info.months.length ? <p className="ar-result-note">No old issues for this paper yet.</p> : null}
      {month && eds === null ? <p className="ar-result-note">Loading issues…</p> : null}
      {eds && eds.length ? (
        <div className="ap-covers">
          {eds.slice(0, shown).map((e) => {
            const s = status[e.day];
            const [wd, date] = dayParts(e.day);
            let action;
            if (!s) action = <span className="ap-act wait">Checking…</span>;
            else if (s.status === "owned") action = <button type="button" className="ap-act ghost" onClick={() => read(e.id)}>Read · in My library</button>;
            else if (s.status === "archive") action = <button type="button" className="ap-act" onClick={() => onBuy(s.edition ? s.edition.id : e.id)}>Buy · {naira(s.price)}</button>;
            else action = <button type="button" className="ap-act ghost" onClick={() => read(e.id)}>Read</button>;
            return (
              <div key={e.id} className="ap-cover">
                <span className="ap-img">{e.image ? <img src={e.image} alt={`${paper.name}, ${date}`} loading="lazy" /> : null}</span>
                <strong>{wd}</strong>
                <span className="ap-date">{date}</span>
                {action}
              </div>
            );
          })}
        </div>
      ) : null}
      {info ? <AskYear paper={paper} haveYears={years} onRequest={onRequest} history={history} /> : null}
      <div className="ap-foot">
        <span>Missing a date? <button type="button" className="ar-link" onClick={() => onRequest(paper)}>Request it free</button>, and we’ll email you when it’s ready.</span>
        {eds && eds.length > shown ? (
          <span><button type="button" className="ar-link" onClick={() => setShown((n) => n + PAGE)}>Show more</button></span>
        ) : null}
      </div>
    </section>
  );
}

export default function ArchivePapers({ browse, onChoose, onBack, onBuy, onRequest, history, reloadKey, initialMonth }) {
  const [papers, setPapers] = useState(null);
  useEffect(() => {
    httpGet("archive_papers").then((r) => setPapers(r && r.data && Array.isArray(r.data.papers) ? r.data.papers : []));
  }, []);

  if (browse) return <PaperView paper={browse} onBack={onBack} onBuy={onBuy} onRequest={onRequest} history={history} reloadKey={reloadKey} initialMonth={initialMonth} />;
  return (
    <section className="ap-section" aria-labelledby="ap-papers">
      <div className="ap-head">
        <h2 id="ap-papers" className="ar-h2">Papers with old issues</h2>
        <span className="ap-note">Only papers with past editions for sale are listed.</span>
      </div>
      {papers === null ? <p className="ar-result-note">Loading papers…</p> : !papers.length ? <p className="ar-result-note">No old issues yet.</p> : (
        <div className="ap-grid">
          {papers.map((p) => (
            <button type="button" key={p.id} className="ap-paper" onClick={() => onChoose(p)}>
              <span className="ap-logo">{p.logo || p.image ? <img src={p.logo || p.image} alt="" loading="lazy" /> : null}</span>
              <span className="ap-text">
                <strong>{p.name}</strong>
                <span>{p.fromPrice ? `Old issues from ${naira(p.fromPrice)}` : "Old issues available"}</span>
              </span>
            </button>
          ))}
        </div>
      )}
    </section>
  );
}
